import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable, of } from "rxjs";
import { finalize, map, shareReplay, tap } from "rxjs/operators";
import { ApprovalProcessAccessContext } from "../interfaces/approval-process-access.interface";
import { WaitingValidationTimeDTO } from "../interfaces/waiting-validation-time.interface";
import { PurchaseOrderUtils } from "../utilities/purchase-order";

@Injectable()
export class ApplicationsService {
  // Shared in-flight requests for the create-if-missing draft endpoint, keyed by contact_id.
  // Concurrent callers (quoter v2, legacy quoter, expedient) reuse the same HTTP request so a
  // single draft is created instead of racing and hitting the backend twice in the same second.
  private readonly applicationWithMostDocumentsOrCreateInFlight = new Map<string, Observable<any>>();

  constructor(private http: HttpClientService) {}

  public getApplications(contact_id, stage?): Observable<any> {
    return this.http
      .get(`/api/contact/${contact_id}/applications`, { params: { stage } })
      .pipe(tap((_) => console.log("ApplicationsService.getApplications: Ok")));
  }

  public getApplicationsFromPerson(person_id, stage?, attributes?): Observable<any> {
    return this.http
      .get(`/api/persons/${person_id}/applications`, { params: { stage, attributes } })
      .pipe(tap((_) => console.log("ApplicationsService.getApplicationsFromPerson: Ok")));
  }

  public resendApprovedApplicationMail(app_id, client_emails: []): Observable<any> {
    const options = {
      client_emails,
    };
    return this.http
      .post(`/api/applications/${app_id}/resend_approval_mailer`, options)
      .pipe(tap((_) => console.log("ApplicationsService.resendApprovedApplicationMail: Ok")));
  }

  public getEvents(app_id): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/workflow_events`)
      .pipe(tap((_) => console.log("ApplicationsService.getEvents: Ok")));
  }

  public updateApplication({
    id,
    approved_request = undefined,
    approved_plan = undefined,
    approval_date = undefined,
    first_downpayment_date = undefined,
    full_downpayment_date = undefined,
    sales_closing_date = undefined,
    sales_closing_reason = undefined,
    deliver_date = undefined,
    stage = undefined,
    won = undefined,
    internal_user_id = undefined,
    external_user_id = undefined,
    validation_process_id = undefined,
    applicants = undefined,
    dealership_id = undefined,
    endorsement_option = undefined,
    endorsement_relation = undefined,
  }): Observable<any> {
    const params = {
      approved_request,
      approved_plan,
      approval_date,
      first_downpayment_date,
      full_downpayment_date,
      sales_closing_date,
      sales_closing_reason,
      deliver_date,
      stage,
      won,
      internal_user_id,
      external_user_id,
      validation_process_id,
      applicants_attributes: applicants,
      dealership_id,
      endorsement_option,
      endorsement_relation,
    };
    return this.http
      .put(`/api/applications/${id}`, params)
      .pipe(tap((_) => console.log("ApplicationsService.updateApplication: Ok")));
  }

  public sendToValidation({
    id,
    approved_request = undefined,
    approved_plan = undefined,
    options = "{}",
  }): Observable<any> {
    const params = {
      approved_request,
      approved_plan,
      options,
    };
    return this.http
      .put(`/api/applications/${id}/send_to_validation`, params)
      .pipe(tap((_) => console.log("ApplicationsService.sendToValidation: Ok")));
  }

  public create({
    contact_id,
    approved_request = undefined,
    approved_plan = undefined,
    approval_date = undefined,
    first_downpayment_date = undefined,
    full_downpayment_date = undefined,
    deliver_date = undefined,
    stage = undefined,
    won = undefined,
    validation_process_id = undefined,
    applicants = undefined,
    dealership_id = undefined,
  }): Observable<any> {
    const params = {
      approved_request,
      approved_plan,
      approval_date,
      first_downpayment_date,
      full_downpayment_date,
      deliver_date,
      stage,
      won,
      validation_process_id,
      applicants_attributes: applicants,
      dealership_id,
    };
    return this.http
      .post(`/api/contact/${contact_id}/applications`, params)
      .pipe(tap((_) => console.log("ApplicationsService.create: Ok")));
  }

  public show(id): Observable<any> {
    return this.http.get(`/api/applications/${id}`).pipe(tap((_) => console.log("ApplicationsService.show: Ok")));
  }

  public approvalProcessAccess(id: number | string): Observable<ApprovalProcessAccessContext> {
    return this.http
      .get<ApprovalProcessAccessContext>(`/api/applications/${id}/approval_process_access`)
      .pipe(tap((_) => console.log("ApplicationsService.approvalProcessAccess: Ok")));
  }

  public getApplicants(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/applicants`)
      .pipe(tap((_) => console.log("ApplicationsService.get_applicants: Ok")));
  }

  public mailOpsDocs(id, stage, comments, purchase_order, contract_data?: any): Observable<any> {
    let params;
    if (stage == "contract") {
      params = { comments, contract_data };
    } else {
      params = {
        comments,
        stage,
        purchase_order,
        contract_data,
      };
    }
    return this.http
      .post(`/api/applications/${id}/complete_docs_mailer${stage == "contract" ? "_contract" : ""}`, params)
      .pipe(tap((_) => console.log("ApplicationsService.mailOpsDocs: Ok")));
  }

  public incidents(id, incidents): Observable<any> {
    const params = {
      id,
      incidents,
    };
    return this.http
      .post(`/api/applications/${id}/incidents`, params)
      .pipe(tap((_) => console.log("ApplicationsService.incidents: Ok")));
  }

  public repuveScrapping(id, vin): Observable<any> {
    const params = {
      id,
      vin,
    };
    return this.http
      .post(`/api/applications/${id}/repuve_scrapping`, params)
      .pipe(tap((_) => console.log("ApplicationsService.repuveScrapping: Ok")));
  }

  public repuveScrappingResponse(id, vin): Observable<any> {
    const options = {
      params: { id, vin },
    };
    return this.http
      .get(`/api/applications/${id}/repuve_scrapping_request`, options)
      .pipe(tap((_) => console.log("ApplicationsService.repuveScrappingRequest: Ok.")));
  }

  public contractCommentsVisit(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/contract_comments_visit`, {})
      .pipe(tap((_) => console.log("ApplicationsService.contractCommentsVisit: Ok.")));
  }

  public resume(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/resume`)
      .pipe(tap((_) => console.log("ApplicationsService.resume: Ok")));
  }

  public approvalResume(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/approval_resume`)
      .pipe(tap((_) => console.log("ApplicationsService.approvalResume: Ok")));
  }

  public downpaymentsResume(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/downpayments_resume`)
      .pipe(tap((_) => console.log("ApplicationsService.downpaymentsResume: Ok")));
  }

  public opsResume({
    from = undefined,
    to = undefined,
    base_from = undefined,
    base_to = undefined,
    stage = undefined,
    group_period = undefined,
    info = undefined,
  }): Observable<any> {
    const options = {
      params: {
        from,
        to,
        base_from,
        base_to,
        stage,
        group_period,
        info,
      },
    };
    return this.http
      .get("/api/applications/ops_resume", options)
      .pipe(tap((_) => console.log("ApplicationsService.opsResume: Ok")));
  }

  public getClosingDateApplications({
    start_date = undefined,
    end_date = undefined,
    ids = undefined,
    name = undefined,
  }): Observable<any> {
    return this.http
      .get("/api/applications/closing_date_applications", {
        params: { start_date, end_date, ids, name },
      })
      .pipe(tap((_) => console.log("ApplicationsService.getClosingDateApplications: Ok")));
  }

  public purchase_order_info(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/purchase_order_info`)
      .pipe(tap((_) => console.log("ApplicationsService.purchase_order_info: Ok")));
  }

  public flux(id: number | string, action: string, usuario_id: number | string, options?: any): Observable<any> {
    const params = {
      flux_action: action,
      options: options || "{}",
      usuario_id,
    };
    return this.http
      .post(`/api/applications/${id}/flux_action`, params)
      .pipe(tap((_) => console.log("ApplicationsService.flux: Ok.")));
  }

  public unreject(id: number | string): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/unreject`, {})
      .pipe(tap((_) => console.log("ApplicationsService.unreject: Ok.")));
  }

  public insuranceReviewNotification(id: number | string): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/insurance_review_notification`, {})
      .pipe(tap((_) => console.log("ApplicationsService.insuranceReviewNotification: Ok.")));
  }

  public validations(id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/approval_process/validations`, {})
      .pipe(tap((_) => console.log("ApplicationsService.validations: Ok.")));
  }

  public address_warning(id: number | string, data): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/approval_process/address_warning`, data)
      .pipe(tap((_) => console.log("ApplicationsService.address_warning: Ok.")));
  }

  public getAllApplications({
    page,
    page_size,
    id = undefined,
    stages,
    signature_date_start = undefined,
    signature_date_end = undefined,
    full_response = undefined,
  }): Observable<any> {
    const options = {
      params: {
        id,
        page,
        page_size,
        "stages[]": stages,
        signature_date_start,
        signature_date_end,
        full_response,
      },
    };
    return this.http
      .get("/api/applications/all", options)
      .pipe(tap((_) => console.log("ApplicationsService.getApplications: Ok")));
  }

  public getCsv(id, stages, signature_date_start, signature_date_end): Observable<any> {
    const options = {
      responseType: "blob" as "json",
      params: {
        id,
        "stages[]": stages,
        signature_date_start,
        signature_date_end,
      },
    };
    return this.http
      .get("/api/applications/to_csv", options)
      .pipe(tap((_) => console.log("ApplicationsService.getCsv: Ok")));
  }

  public getPurchaseOrder(application_id: number): Observable<any> {
    return this.http.get(`/api/applications/${application_id}/purchase_order`).pipe(
      map((response: any) => PurchaseOrderUtils.transformPurchaseOrderResponse(response)),
      tap((_) => console.log("ApplicationsService.getPurchaseOrder: Ok."))
    );
  }

  public stpTransactionsWithoutDocument(application_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/stp_transactions_without_document`)
      .pipe(tap((_) => console.log("ApplicationsService.stpTransactionsWithoutDocument: Ok.")));
  }

  public getCommissionOptions(application_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/commission_options`)
      .pipe(tap((_) => console.log("ApplicationsService.getCommissionOptions: Ok.")));
  }

  public getMissingCommissionApplicationsByUser(filters): Observable<any[]> {
    const options = {
      params: filters,
    };
    return this.http
      .get("/api/applications/search_for_commission_by_user", options)
      .pipe(tap((_) => console.log("ApplicationsService.getMissingCommissionApplicationsByUser: Ok.")));
  }

  public findOrCreatePurchaseOrder(application_id: number, prevalidation = "false") {
    const options = {
      params: { prevalidation },
    };
    return this.http
      .get(`/api/applications/${application_id}/find_or_create_purchase_order`, options)
      .pipe(tap((_) => console.log("ApplicationsService.findOrCreatePurchaseOrder: Ok.")));
  }

  public getAppForPurchaseOrder(application_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/for_purchase_order`)
      .pipe(tap((_) => console.log("ApplicationsService.getAppForPurchaseOrder: Ok.")));
  }

  public getVin(application_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/vin`)
      .pipe(tap((_) => console.log("ApplicationsService.getVin: Ok.")));
  }

  public getQuoteResume(application_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/quote_resume`)
      .pipe(tap((_) => console.log("ApplicationsService.getQuoteResume: Ok.")));
  }

  public getCarInfoQuoteResume(application_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/car_info_quote_resume`)
      .pipe(tap((_) => console.log("ApplicationsService.getCarInfoQuoteResume: Ok.")));
  }

  public getApplicationBuroName(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/application_buro_name`)
      .pipe(tap((_) => console.log("ApplicationsService.getApplicationBuroName: Ok.")));
  }

  public getSimpleAddresses(id) {
    return this.http
      .get(`/api/applications/${id}/simple_addresses`)
      .pipe(tap((_) => console.log("ApplicationsService.getSimpleAddresses: Ok.")));
  }

  public getAddresses(id, mode: "distinct" | undefined = undefined, categories: string[] | undefined = undefined) {
    // note: joins categories with comma due to rails and http get method params parsing not supporting arrays
    return this.http
      .get(`/api/applications/${id}/addresses`, { params: { mode, categories: categories?.join(",") } })
      .pipe(tap((_) => console.log("ApplicationsService.getAddresses: Ok.")));
  }

  // # GET /api/applications/:id/validation_processes/deleted_survey_addresses
  public getDeletedSurveyAddresses(id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/validation_processes/deleted_survey_addresses`)
      .pipe(tap((_) => console.log("ApplicationsService.getDeletedSurveyAddresses: Ok.")));
  }

  public saveApplicants(application_id: number, applicants: any): Observable<any> {
    const params = {
      applicants_attributes: applicants,
    };
    return this.http
      .put(`/api/applications/${application_id}/save_applicants`, params)
      .pipe(tap((_) => console.log("ApplicationsService.saveApplicants: Ok")));
  }

  public getDataBeforeSendValidation(contact_id) {
    return this.http
      .get(`/api/contact/${contact_id}/applications/data_before_send_validation`)
      .pipe(tap((_) => console.log("ApplicationsService.data_before_send_validation: Ok")));
  }

  public createBeforeValidate(contact_id): Observable<any> {
    return this.http
      .post(`/api/contact/${contact_id}/applications/create_before_validation`, {})
      .pipe(tap((_) => console.log("ApplicationsService.createBeforeValidate: Ok")));
  }

  public getReferences(application_id): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/references`)
      .pipe(tap((_) => console.log("ApplicationsService.getReferences: Ok")));
  }

  public getWithPayments(filters): Observable<any> {
    const options = {
      params: filters,
    };
    return this.http
      .get("/api/applications/with_payments", options)
      .pipe(tap((_) => console.log("ApplicationsService.getWithPayments: Ok.")));
  }

  public getPayments(application_id): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/payments`)
      .pipe(tap((_) => console.log("ApplicationsService.getPayments: Ok.")));
  }

  public notifyClientCall(application_id) {
    return this.http
      .post(`/api/applications/${application_id}/client_call_notification`, undefined)
      .pipe(tap((_) => console.log("ApplicationsService.notifyClientCall: Ok.")));
  }

  public notifyClientEpCall(application_id) {
    return this.http
      .post(`/api/applications/${application_id}/client_ep_call_notification`, undefined)
      .pipe(tap((_) => console.log("ApplicationsService.notifyClientEpCall: Ok.")));
  }

  public addProofAddress(
    application_id: number | string,
    address: string,
    address_type: string,
    add_if_exist_type = false
  ): Observable<any> {
    const params = {
      address,
      address_type,
      add_if_exist_type,
    };
    return this.http
      .post(`/api/applications/${application_id}/add_proof_address`, params)
      .pipe(tap((_) => console.log("ApplicationsService.addProofAddress: Ok.")));
  }

  public getInsuranceDataForPolicy(application_id): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/insurance_data_for_policy`)
      .pipe(tap((_) => console.log("ApplicationsService.getPayments: Ok.")));
  }

  public getAppForContract(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/contract_initial_payment_validation`)
      .pipe(tap((_) => console.log("ApplicationsService.getAppForContract: Ok.")));
  }

  public getCarKeysLetter(application_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/car_keys_acceptance_of_responsibility`, {
        responseType: "blob" as "json",
      })
      .pipe(tap((_) => console.log("ApplicationsService.getCarKeysLetter: Ok.")));
  }

  public getApprovedAddress(application_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/approved_address`)
      .pipe(tap((_) => console.log("ApplicationsService.getApprovedAddress: OK")));
  }

  public getStpTransactions(app_id): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/stp_payment_info`)
      .pipe(tap((_) => console.log("ApplicationsService.getStpTransactions: Ok")));
  }

  public getIncomeAnalysisInfo(app_id): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/income_analysis_info`)
      .pipe(tap((_) => console.log("ApplicationsService.getIncomeAnalysisInfo: Ok")));
  }

  public getIncomeAnalysisInfoKeys(app_id, keys = []): Observable<any> {
    return this.http
      .post(`/api/applications/${app_id}/income_analysis_info_keys`, { keys })
      .pipe(tap((_) => console.log("ApplicationsService.getIncomeAnalysisInfo: Ok")));
  }

  public getIncomeAnalysisTransactions(app_id: number, clabe: string, page = 1, limit = 100): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/income_analysis_transactions`, {
        params: { clabe, page: String(page), limit: String(limit) },
      })
      .pipe(tap((_) => console.log("ApplicationsService.getIncomeAnalysisTransactions: Ok")));
  }

  public getRisksInconsistencies(app_id): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/risks_inconsistencies`)
      .pipe(tap((_) => console.log("ApplicationsService.getRisksInconsistencies: Ok")));
  }

  public sendCifInfo(
    {
      rfc,
      first_name,
      middle_name,
      first_last_name,
      second_last_name,
      tax_regime,
      fiscal_name,
      use_person_information,
      street,
      interior_number,
      exterior_number,
      postal_code,
      sublocality,
    },
    id
  ): Observable<any> {
    const params = {
      rfc,
      first_name,
      middle_name,
      first_last_name,
      second_last_name,
      tax_regime,
      fiscal_name,
      use_person_information,
      street,
      interior_number,
      exterior_number,
      postal_code,
      sublocality,
    };
    return this.http
      .post(`/api/applications/${id}/update_invoice`, params)
      .pipe(tap((_) => console.log("ApplicationsService.sendCifInfo: Ok")));
  }

  public invoiceUploaded(id: number): Observable<{ invoice_uploaded: boolean }> {
    return this.http
      .get(`/api/applications/${id}/invoice_uploaded`)
      .pipe(tap((_) => console.log("ApplicationsService.isInvoiceUploaded: Ok")));
  }

  public cifData(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/cif_data`)
      .pipe(tap((_) => console.log("ApplicationsService.cifData: Ok")));
  }

  public highRiskValidationQuestions(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/high_risk_data_questions`)
      .pipe(tap((_) => console.log("ApplicationsService.highRiskValidationQuestions: Ok")));
  }

  public getFakeAddressQuestion(app_id: any): Observable<any> {
    return this.http
      .get("/api/validation_questions/fake_address", { params: { application_id: app_id } })
      .pipe(tap((_) => console.log("ApplicationsService.getFakeAddress: Ok")));
  }

  public abortOnboarding(id): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/onboarding/abort`, {})
      .pipe(tap((_) => console.log("ApplicationsService.abortOnboarding: Ok")));
  }

  public sendOnboardingLink(id: string | number): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/onboarding/send_link`, {})
      .pipe(tap((_) => console.log("ApplicationsService.sendOnboardingLink: Ok")));
  }

  public createPdvEvent(id, { category, subcategory, name, details = {}, allow_duplicate = false }): Observable<any> {
    const params = {
      allow_duplicate,
      event: { category, subcategory, name, details, created_at: new Date().toISOString() },
    };
    return this.http
      .post(`/api/applications/${id}/create_pdv_event`, params)
      .pipe(tap((_) => console.log("ApplicationsService.createPdvEvent: Ok")));
  }

  public getFolderAnaliyst(app_id): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/folder_analyst`)
      .pipe(tap((_) => console.log("ApplicationsService.getFolderAnaliyst: Ok")));
  }

  public getCompleteAddress(app_id): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/complete_address`)
      .pipe(tap((_) => console.log("ApplicationsService.getCompleteAddress: Ok")));
  }

  public getDocuments(app_id: string | number): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/documents`)
      .pipe(tap((_) => console.log("ApplicationsService.getDocuments: Ok")));
  }

  public notifyDocumentSafeguarding(application_id: number): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/notify_document_safeguarding`, undefined)
      .pipe(tap((_) => console.log("ApplicationsService.notifyDocumentSafeguarding: Ok.")));
  }

  public getPlatesProcessDocsSafeguardingDate(app_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/plates_process_docs_safeguarding_date`)
      .pipe(tap((_) => console.log("ApplicationsService.getPlatesProcessDocsSafeguardingDate: Ok")));
  }

  public getPlatesProcess(app_id: number): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/plates_process`)
      .pipe(tap((_) => console.log("ApplicationsService.getPlatesProcess: Ok")));
  }

  public getWorkflowMetadata(app_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${app_id}/workflow_metadata`)
      .pipe(tap((_) => console.log("ApplicationsService.getWorkflowMetadata: Ok")));
  }

  public getCurrentStageInformation(id: string, is_authenticated: boolean): Observable<any> {
    if (is_authenticated === true) {
      return this.http
        .get(`/api/applications/${id}/current_stage_information`)
        .pipe(tap((_) => console.log("ApplicationsService.getCurrentStageInformation: Ok")));
    } else {
      return this.http
        .getWithoutAuth(`/api/applications/${id}/current_stage_information`)
        .pipe(tap((_) => console.log("ApplicationsService.getCurrentStageInformation: Ok")));
    }
  }

  public setApplicationNotificationsValue(id: string, active_notifications: boolean): Observable<any> {
    const params = {
      active_notifications,
    };
    return this.http
      .put(`/api/applications/${id}`, params)
      .pipe(tap((_) => console.log("ApplicationsService.setApplicationNotificationsValue: Ok")));
  }

  public getContractParams(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/contract_params`)
      .pipe(tap((_) => console.log("ApplicationsService.getContractParams: Ok")));
  }

  public getAnalysisOverview(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/analysis_overview`)
      .pipe(tap((_) => console.log("ApplicationsService.getAnalysisOverview: Ok")));
  }

  public getScoreAlerts(application_id: number | string, category = "", step = ""): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/score_alerts`, { params: { category, step } })
      .pipe(tap((_) => console.log("ApplicationsService.getScoreAlerts: Ok.")));
  }

  public getScoreAlertsKeys(application_id: number | string, category = "", step = "", keys = []): Observable<any> {
    const params = {
      category,
      step,
      keys,
    };
    return this.http
      .post(`/api/applications/${application_id}/score_alerts_keys`, params)
      .pipe(tap((_) => console.log("ApplicationsService.getScoreAlertsKeys: Ok.")));
  }

  public resendOnboardingLink(id: string | number): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/onboarding/resend_link`, {})
      .pipe(tap((_) => console.log("ApplicationsService.resendOnboardingLink: Ok")));
  }

  public completeOnboarding(id: string | number, body: object = {}): Observable<any> {
    return this.http
      .put(`/api/applications/${id}/onboarding/complete_onboarding`, body)
      .pipe(tap((_) => console.log("ApplicationsService.completeOnboarding: Ok")));
  }

  public setManualIncomeValidation(application_id: number): Observable<any> {
    return this.http
      .put(`/api/applications/${application_id}/manual_income_validation`, {})
      .pipe(tap((_) => console.log("ApplicationsService.setManualIncomeValidation: Ok")));
  }

  public getFinishApprovalData(id: string | number): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/finish_approval_data`, {})
      .pipe(tap((_) => console.log("ApplicationsService.getFinishApprovalData: Ok")));
  }

  public performAutomaticValidations(id: string | number): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/perform_automatic_validations`, {})
      .pipe(tap((_) => console.log("ApplicationsService.performAutomaticValidations: Ok")));
  }

  public cancelOnboarding(app_id: number): Observable<any> {
    return this.http
      .post(`/api/applications/${app_id}/onboarding/cancel_onboarding`, {})
      .pipe(tap((_) => console.log("ApplicationsService.cancelOnboarding: Ok.")));
  }

  public skipOnboardingStep(app_id: number): Observable<any> {
    return this.http
      .post(`/api/applications/${app_id}/onboarding/skip_step`, {})
      .pipe(tap((_) => console.log("ApplicationsService.skipOnboardingStep: Ok.")));
  }

  public retryIdentityValidation(application_id: number): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/onboarding/retry_identity_validation`, {})
      .pipe(tap((_) => console.log("ApplicationsService.retryIdentityValidation: Ok.")));
  }

  // # POST /api/applications/:id/perform_overall_score
  public performOverallScore(id: string | number): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/perform_overall_score`, {})
      .pipe(tap((_) => console.log("ApplicationsService.performOverallScore: Ok")));
  }

  public getAiWarnings<Warning = { type: string; severity: string; message: string; title: string }>(
    application_id: number | string
  ): Observable<Array<Warning>> {
    return this.http
      .get(`/api/applications/${application_id}/ai_warnings`)
      .pipe(tap((_) => console.log("ApplicationsService.getAiWarnings: Ok")));
  }

  public getDocumentalEvaluation(application_id: number) {
    return this.http
      .get(`/api/applications/${application_id}/documental_evaluation`)
      .pipe(tap((_) => console.log("ApplicationsService.getDocumentalEvaluation: Ok")));
  }

  public enqueueDocumentalEvaluation(
    application_id: number,
    { trigger_event }: { trigger_event: string }
  ): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/enqueue_documental_evaluation`, { trigger_event })
      .pipe(tap((_) => console.log("ApplicationsService.enqueueDocumentalEvaluation: Ok")));
  }

  public gatherDocumentDataExtraction(application_id: number | string): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/gather_doc_data_extraction`, {})
      .pipe(tap((_) => console.log("ApplicationsService.gatherDocumentDataExtraction: Ok")));
  }

  public getValidationDataStatus<TResponse = any>(application_id: number | string): Observable<TResponse> {
    return this.http
      .get(`/api/applications/${application_id}/validation_data_status`)
      .pipe(tap((_) => console.log("ApplicationsService.getValidationDataStatus: Ok")));
  }

  public saveWarningReport<Warning extends { type: string }>(
    application_id: number | string,
    warning: Warning,
    comment: string
  ): Observable<any> {
    const params = {
      warning_type: warning.type,
      comment,
      details: warning,
    };
    return this.http
      .post(`/api/applications/${application_id}/warning_reports`, params)
      .pipe(tap((_) => console.log("ApplicationsService.saveWarningReport: Ok")));
  }

  public saveWaitingValidationTime(
    application_id: number,
    validation_waiting_time: WaitingValidationTimeDTO
  ): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/validation_waiting_time`, { validation_waiting_time })
      .pipe(tap((_) => console.log("ApplicationsService.saveValidationWaitingTime: Ok")));
  }

  // # POST /api/applications/:id/quick_approval
  public quickApproval(id: string | number): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/quick_approval`, {})
      .pipe(tap((_) => console.log("ApplicationsService.quickApproval: Ok")));
  }

  // # GET /api/applications/:id/quick_approval_status
  public quickApprovalStatus(id: string | number): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/quick_approval_status`, {})
      .pipe(tap((_) => console.log("ApplicationsService.quickApprovalStatus: Ok")));
  }

  public getAutomaticValidationProof(application_id: number | string, category: string): Observable<any> {
    const options = {
      params: { category },
    };
    return this.http
      .get(`/api/applications/${application_id}/valid_automatic_validation_proof`, options)
      .pipe(tap((_) => console.log("ApplicationsService.getAutomaticValidationProof: Ok.")));
  }

  public getTitularPhoneNumbers(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/titular_phone_numbers`)
      .pipe(tap((_) => console.log("ApplicationsService.getTitularPhoneNumbers: Ok.")));
  }

  // # GET /api/applications/:id/stp_validation
  public getStpValidation(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/stp_validation`)
      .pipe(tap((_) => console.log("ApplicationsService.getStpValidation: Ok.")));
  }

  // # POST /api/applications/:id/workflow/workflow_status_history/nested_state
  public upsertWorkflowNestedState(
    application_id: number | string,
    step_name: string,
    { started_at = undefined, finished_at = undefined }
  ): Observable<any> {
    const params = {
      step_name,
      started_at,
      finished_at,
    };
    return this.http
      .post(`/api/applications/${application_id}/upsert_workflow_history_nested_state`, params)
      .pipe(tap((_) => console.log("ApplicationsService.getWorkflowStatusHistoryNestedState: Ok.")));
  }

  public notifyUpdatedDefaultCommissionPaymentBankAccount(id: number | string, params) {
    return this.http
      .post(`/api/applications/${id}/updated_default_commission_payment_bank_account`, params)
      .pipe(tap((_) => console.log("ApplicationsService.notifyUpdatedDefaultCommissionPaymentBankAccount: Ok.")));
  }

  public getProfileInvestigationQuestion(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/profile_investigation_question`)
      .pipe(tap((_) => console.log("ApplicationsService.getProfileInvestigationQuestion: Ok.")));
  }

  public getEmployeeProfile(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/employee_profile`)
      .pipe(tap((_) => console.log("ApplicationsService.getEmployeeProfile: Ok.")));
  }

  // # POST /api/applications/:app_id/web_search_validation/find_or_create
  public findOrCreateWebSearchValidation(application_id: number | string): Observable<any> {
    const params = {};
    return this.http
      .post(`/api/applications/${application_id}/web_search_validation/find_or_create`, params)
      .pipe(tap((_) => console.log("ApplicationsService.findOrCreateWebSearchValidation: Ok.")));
  }

  // # PUT /api/applications/:id/web_search_validation/update
  public updateWebSearchValidation(
    application_id: number | string,
    {
      status = "pending", // default status is 'pending'
      total_duration = undefined,
    }
  ): // Add any other parameters you might need
  Observable<any> {
    const params = {
      status,
      total_duration,
    };
    return this.http
      .put(`/api/applications/${application_id}/web_search_validation/update`, params)
      .pipe(tap((_) => console.log("ApplicationsService.updateWebSearchValidation: Ok.")));
  }

  // # GET /api/applications/:id/video_verification_session
  public getVideoVerificationSession(application_id: number | string, purpose = "analysis"): Observable<any> {
    const options = { params: { purpose } };
    return this.http
      .get(`/api/applications/${application_id}/video_verification_session`, options)
      .pipe(tap((_) => console.log("ApplicationsService.getVideoVerificationSession: Ok.")));
  }

  // # POST /api/applications/:id/force_video_verification_session
  public forceVideoVerificationSession(application_id: number | string): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/force_video_verification_session`, {})
      .pipe(tap((_) => console.log("ApplicationsService.forceVideoVerificationSession: Ok.")));
  }

  // # POST /api/contact/:id/applications/application_with_most_documents_or_create
  public getApplicationWithMostDocumentsOrCreate({
    contact_id,
    approved_request = undefined,
    approved_plan = undefined,
    approval_date = undefined,
    first_downpayment_date = undefined,
    full_downpayment_date = undefined,
    deliver_date = undefined,
    stage = undefined,
    won = undefined,
    validation_process_id = undefined,
    applicants = undefined,
    dealership_id = undefined,
  }): Observable<any> {
    // Dedupe concurrent calls per contact so we never create two drafts in the same window.
    const inFlightKey = String(contact_id);
    const pending = this.applicationWithMostDocumentsOrCreateInFlight.get(inFlightKey);
    if (pending) {
      return pending;
    }

    const params = {
      approved_request,
      approved_plan,
      approval_date,
      first_downpayment_date,
      full_downpayment_date,
      deliver_date,
      stage,
      won,
      validation_process_id,
      applicants_attributes: applicants,
      dealership_id,
    };
    const request$ = this.http
      .post(`/api/contact/${contact_id}/applications/application_with_most_documents_or_create`, params)
      .pipe(
        tap((_) => console.log("ApplicationsService.getApplicationWithMostDocumentsOrCreate: Ok")),
        // Release the lock once the request settles (success or error) so later calls can retry.
        finalize(() => this.applicationWithMostDocumentsOrCreateInFlight.delete(inFlightKey)),
        // Share the single request among concurrent subscribers without re-triggering the POST.
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.applicationWithMostDocumentsOrCreateInFlight.set(inFlightKey, request$);
    return request$;
  }

  // # GET /api/applications/:id/score_risk_matrix_history
  public getScoreRiskMatrixHistory(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/score_risk_matrix_history`, {})
      .pipe(tap((_) => console.log("ApplicationsService.getScoreRiskMatrixHistory: Ok.")));
  }
  // # GET /api/applications/:id/score_risk_matrix_events
  public getScoreRiskMatrixEvents(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/score_risk_matrix_events`, {})
      .pipe(tap((_) => console.log("ApplicationsService.getScoreRiskMatrixEvents: Ok.")));
  }

  // # POST /api/applications/:id/create_estimated_income
  public createEstimatedIncome(application_id: number | string): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/create_estimated_income`, {})
      .pipe(tap((_) => console.log("ApplicationsService.createEstimatedIncome: Ok.")));
  }

  // # GET /api/applications/:id/estimated_income
  public getEstimatedIncome(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/estimated_income`, {})
      .pipe(tap((_) => console.log("ApplicationsService.getEstimatedIncome: Ok.")));
  }

  public addAdditionalPhone(id, phone, origin, category) {
    const params = {
      id,
      phone_numbers: phone,
      origin,
      category,
    };

    return this.http
      .post(`/api/applications/${id}/phone_numbers`, params)
      .pipe(tap((_) => console.log("ApplicationsService.addAdditionalPhone: Ok.")));
  }

  public getAdditionalPhones(id): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/phone_numbers`)
      .pipe(tap((_) => console.log("ApplicationsService.getAdditionalPhones: Ok.")));
  }

  public removeInactiveStatements(id): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/remove_inactive_statements`, {})
      .pipe(tap((_) => console.log("ApplicationsService.removeInactiveStatements: Ok.")));
  }

  public getApprovedRequest(id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/approved_request`)
      .pipe(tap((_) => console.log("ApplicationsService.getApprovedRequest: Ok")));
  }

  public getInitialConditionsForApprovalProcess(id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/initial_conditions_for_approval_process`)
      .pipe(tap((_) => console.log("ApplicationsService.getInitialConditionsForApprovalProcess: Ok")));
  }

  public getRequiredIncomeValidation(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/required_income_validation`)
      .pipe(tap((_) => console.log("ApplicationsService.getRequiredIncomeValidation: Ok")));
  }

  public requiredIncomeValidation(
    application_id: number | string,
    required_income: number,
    quote: any
  ): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/required_income_validation`, { required_income, quote })
      .pipe(tap((_) => console.log("ApplicationsService.createRequiredIncomeValidation: Ok")));
  }

  public updateRequiredIncomeValidation(
    application_id: number | string,
    params: {
      mitigantes?: Record<string, unknown>;
      bypass_reason?: string;
    }
  ): Observable<any> {
    return this.http
      .post(`/api/applications/${application_id}/required_income_validation/update`, params)
      .pipe(tap((_) => console.log("ApplicationsService.bypassRequiredIncomeValidation: Ok")));
  }

  public resetCommissions(id: number | string): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/reset_commissions`, {})
      .pipe(tap((_) => console.log("ApplicationsService.resetCommissions: Ok")));
  }

  public getKycProfile(application_id: number | string): Observable<any> {
    return this.http
      .get(`/api/applications/${application_id}/kyc_profile`)
      .pipe(tap((_) => console.log("ApplicationsService.getKycProfile: Ok")));
  }

  public recoverExpiredOnboarding(id: string | number): Observable<any> {
    return this.http
      .post(`/api/applications/${id}/onboarding/recover_expired_onboarding`, {})
      .pipe(tap((_) => console.log("ApplicationsService.recoverExpiredOnboarding: Ok")));
  }

  public getApplicationStageTracking(id: string | number): Observable<any> {
    return this.http
      .get(`/api/applications/${id}/stage_tracking`)
      .pipe(tap((_) => console.log("ApplicationsService.getApplicationStageTracking: Ok")));
  }
}
